// Onboarding state — replaces the onboarding.js localStorage keys
// (pwa-chat-pending-tags / -mood / -onboarding-done) and the server `onboarded`
// flag cache. `onboarded` is null until known; pendingTags/pendingMood carry
// pre-auth selections across the register step; pendingStyle is the baseline
// funnel's communication-style choice.
//
// pendingName / pendingAddress / pendingTone carry the step 1-2 profile
// (name, grammatical gender, tone) across the register step for the post-auth
// onboarding gate. pendingAddress holds the canonical token
// ('feminine' | 'masculine') sent to POST /onboard as address_gender — the
// chip label never leaves the device.
//
// `onboardedForUserId` stamps WHICH user the cached `onboarded` flag belongs
// to. localStorage is per-browser, not per-user — so when tester swap account
// in same browser, old account's "yes onboarded" cache would leak to new
// account and skip the onboarding screen (bd-1am). Stamp the user id every
// time we set the flag; gate checks both before trusting cache.

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { createPersistentStorage } from '../lib/persistentStorage'

interface OnboardingState {
  onboarded: boolean | null // null = unknown, true = done (server source of truth cached)
  onboardedForUserId: string | null // who cache belong to? null = nobody yet
  pendingTags: string[] | null
  pendingMood: number | null
  pendingStyle: string | null
  pendingName: string | null
  pendingAddress: string | null
  pendingTone: string | null
  setOnboarded: (v: boolean | null, userId?: string | null) => void
  setPending: (tags: string[], mood: number, style?: string | null) => void
  setLocalProfile: (name: string, address: string, tone: string) => void
  clearPending: () => void
  reset: () => void
}

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set) => ({
      onboarded: null,
      onboardedForUserId: null,
      pendingTags: null,
      pendingMood: null,
      pendingStyle: null,
      pendingName: null,
      pendingAddress: null,
      pendingTone: null,

      // v = new flag value; userId = stamp this user as owner of cache.
      // No userId? owner wiped (treat as "cache for nobody"). Callers that
      // KNOW current user should pass it; bare `setOnboarded(null)` clears.
      setOnboarded: (v, userId) =>
        set({ onboarded: v, onboardedForUserId: userId ?? null }),

      setPending: (tags, mood, style) =>
        set({ pendingTags: tags, pendingMood: mood, pendingStyle: style ?? null }),
      setLocalProfile: (name, address, tone) =>
        set({ pendingName: name, pendingAddress: address, pendingTone: tone }),
      clearPending: () =>
        set({
          pendingTags: null,
          pendingMood: null,
          pendingStyle: null,
          pendingName: null,
          pendingAddress: null,
          pendingTone: null,
        }),
      reset: () =>
        set({
          onboarded: null,
          onboardedForUserId: null,
          pendingTags: null,
          pendingMood: null,
          pendingStyle: null,
          pendingName: null,
          pendingAddress: null,
          pendingTone: null,
        }),
    }),
    { name: 'humanteq-onboarding', storage: createPersistentStorage() }
  )
)
